import type { AlternativesPosition } from "@/model/detailedValuation/AlternativesPosition";
import type { FieldSpecKind } from "../../../operations/drawer/templates";
import type { BondPosition } from "@/model/detailedValuation/BondPosition";
import type { CashPosition } from "@/model/detailedValuation/CashPosition";
import type { EquityPosition } from "@/model/detailedValuation/EquityPosition";
import type { FxForwardPosition } from "@/model/detailedValuation/FxForwardPosition";
import type { OthersPosition } from "@/model/detailedValuation/OthersPosition";
import type { CustomCellRendererProps } from "ag-grid-react";
import { isNotDefined } from "@/utils/assertions";

export type PositionTypeName = 'CASH' | 'BOND' | 'ALTERNATIVE' | 'EQUITY' | 'FORWARD' | 'MISCELLANEOUS'

export type PositionType = AlternativesPosition | BondPosition | CashPosition | EquityPosition | FxForwardPosition | OthersPosition

export const mapAssetClassToPositionTypeName = (assetClass: string) : PositionTypeName => {
  switch (assetClass) {
    case 'ALTERNATIVE': return 'ALTERNATIVE'
    case 'BOND': return 'BOND'
    case 'CASH': return 'CASH'
    case 'EQUITIES': return 'EQUITY'
    case 'FORWARDS': return 'FORWARD'
    default: return 'MISCELLANEOUS'
  }
}

export type FieldSpec =
  | string // just the field name
  | {
    fieldName: string;
    kind: FieldSpecKind,
  };

export function getFieldName(fieldSpec: FieldSpec): string {
  return typeof fieldSpec === 'string' ? fieldSpec : fieldSpec.fieldName;
}

interface Tab {
  id: string;
  fields: FieldSpec[];
}

export interface Template {
  headerFields: FieldSpec[];
  tabs: Tab[];
  type: PositionTypeName;
  title: FieldSpec;
}

const templates: { [code in PositionTypeName]: Template } = {
  ALTERNATIVE: {
    title: 'isin',
    type: 'ALTERNATIVE',
    headerFields: [
      'name', 'currency',
      'locationPlace', { fieldName: 'quantity', kind: 'number' },
      'instrumentCode', { fieldName: 'negotiateQuantity', kind: 'number' },
      // 'pledged', { fieldName: 'netPurchasePrice', kind: 'amount' },
      'quotationPlaceName',
    ],
    tabs: [
      /*  {
          id: 'amounts',
          fields: [
            { fieldName: 'amount', kind: 'number' },
            // { fieldName: 'amountCounterValue', kind: 'number' },
            { fieldName: 'counterValue', kind: 'number' },
            { fieldName: 'profitAndLoss', kind: 'number' },
            { fieldName: 'averagePurchasePrice', kind: 'number' },
            { fieldName: 'variation', kind: 'percent' },
            { fieldName: 'counterValueVariation', kind: 'percent' },
          ]
        },
        {
          id: 'history',
          fields: [
            { fieldName: 'openingDate', kind: 'date' },
            { fieldName: 'lastPrice', kind: 'amount' },
            { fieldName: 'lastPriceDate', kind: 'date' },
            { fieldName: 'lastMovementDate', kind: 'date' },
            // { fieldName: 'lastPurchaseDate', kind: 'date' },
          ]
        },
        {
          id: 'performance',
          fields: [
            { fieldName: 'perfYearToDatePercent', kind: 'percent' },
            { fieldName: 'portfolioWeight', kind: 'percent' },
            { fieldName: 'assetWeight', kind: 'percent' },
          ]
        } */
    ]
  },
  BOND: {
    title: 'isin',
    type: 'BOND',
    headerFields: [
      'name', 'currency',
      'locationPlace', // 'instrumentCode',
      { fieldName: 'quantity', kind: 'number' }, // { fieldName: 'purchasePrice', kind: 'amount' },
      // 'pledged',
      'quotationPlaceName', // 'sectorName',
      { fieldName: 'negotiateQuantity', kind: 'number' },
      // { fieldName: 'moodysRating', kind: 'number' }, { fieldName: 'stdAndPoorRating', kind: 'number' },
      // { fieldName: 'rate', kind: 'number' }, // 'bloomBergTicker',
    ],
    tabs: [
      /* {
        id: 'amounts',
        fields: [
          { fieldName: 'amount', kind: 'number' },
          // { fieldName: 'amountCounterValue', kind: 'number' },
          { fieldName: 'counterValue', kind: 'number' },
          { fieldName: 'profitAndLoss', kind: 'number' },
          { fieldName: 'currentPrice', kind: 'number' },
          { fieldName: 'variation', kind: 'percent' },
          { fieldName: 'counterValueVariation', kind: 'percent' },
          { fieldName: 'accruedInterestAmount', kind: 'number' },
          // { fieldName: 'receivedCouponNetAmount', kind: 'number' },
        ]
      },
      {
        id: 'history',
        fields: [
          { fieldName: 'dueDate', kind: 'date' },
          { fieldName: 'lastMovementDate', kind: 'date' },
          // { fieldName: 'lastCouponDate', kind: 'date' },
          // { fieldName: 'callDate', kind: 'date' },
          { fieldName: 'priceDate', kind: 'date' },
        ]
      },
      {
        id: 'performance',
        fields: [
          { fieldName: 'portfolioWeight', kind: 'percent' },
          // { fieldName: 'assetWeight', kind: 'percent' },
          // { fieldName: 'gainMaturityLossVsBuy', kind: 'number' },
        ]
      } */
    ],
  },
  CASH: {
    title: 'isin',
    type: 'CASH',
    headerFields: [],
    tabs: [],
  },
  EQUITY: {
    title: 'isin',
    type: 'EQUITY',
    headerFields: [
      'name', 'currency',
      'locationPlace', // 'instrumentCode',
      { fieldName: 'quantity', kind: 'number' },
      'quotationPlaceName', { fieldName: 'negotiateQuantity', kind: 'number' },
      // 'quotationPlaceMicCode',
      // 'pledged', 'bloomBergTicker',
    ],
    tabs: [
      /* {
         id: 'amounts',
         fields: [
           { fieldName: 'amount', kind: 'number' },
           // { fieldName: 'amountCounterValue', kind: 'number' },
           { fieldName: 'counterValue', kind: 'number' },
           { fieldName: 'profitAndLoss', kind: 'number' },
           { fieldName: 'variation', kind: 'percent' },
           // { fieldName: 'counterValueVariation', kind: 'percent' },
           { fieldName: 'averagePurchasePrice', kind: 'number' },
           { fieldName: 'unitCostPrice', kind: 'number' },
         ]
       },
       {
         id: 'history',
         fields: [
           { fieldName: 'openingDate', kind: 'date' },
           { fieldName: 'lastMovementDate', kind: 'date' },
           { fieldName: 'lastPurchaseDate', kind: 'date' },
           { fieldName: 'lastPrice', kind: 'number' },
           { fieldName: 'lastPriceDate', kind: 'date' },
         ]
       },
       {
         id: 'performance',
         fields: [
           // { fieldName: 'perfYearToDatePercent', kind: 'percent' },
           { fieldName: 'portfolioWeight', kind: 'percent' },
           { fieldName: 'assetWeight', kind: 'percent' },
         ]
       }*/
    ],
  },
  FORWARD: {
    title: 'backOfficeRef',
    type: 'FORWARD',
    headerFields: [],
    tabs: [],
  },
  MISCELLANEOUS: {
    title: 'isin',
    type: 'MISCELLANEOUS',
    headerFields: [
      'name', 'currency',
      'locationPlace', // 'instrumentCode',
      { fieldName: 'quantity', kind: 'number' },
      // 'pledged', 
      'quotationPlaceName', { fieldName: 'negotiateQuantity', kind: 'number' },
      // 'underlyingName', 'underlyingIsin',
      // 'instrument',
    ],
    tabs: [
      /* {
         id: 'amounts',
         fields: [
           { fieldName: 'quantity', kind: 'number' },
           { fieldName: 'amount', kind: 'number' },
           // { fieldName: 'amountCounterValue', kind: 'number' },
           { fieldName: 'counterValue', kind: 'number' },
         ]
       },
       {
         id: 'history',
         fields: [
           { fieldName: 'lastPrice', kind: 'amount' },
           { fieldName: 'lastPriceDate', kind: 'date' },
           { fieldName: 'lastMovementDate', kind: 'date' },
           { fieldName: 'maturityDate', kind: 'date' },
         ]
       },
       {
         id: 'performance',
         fields: [
           { fieldName: 'portfolioWeight', kind: 'percent' },
           { fieldName: 'assetWeight', kind: 'percent' },
         ]
       }*/
    ]
  }
};

const templatesForFims: { [code in PositionTypeName]: Template } = {
  ALTERNATIVE: {
    title: 'isin',
    type: 'ALTERNATIVE',
    headerFields: [
      'name', 'currency',
      'locationPlace', { fieldName: 'quantity', kind: 'number' },
      'instrumentCode', { fieldName: 'negotiateQuantity', kind: 'number' },
      // 'pledged', 
      'quotationPlaceName',
    ],
    tabs: [
      /*  {
          id: 'amounts',
          fields: [
            { fieldName: 'amount', kind: 'number' },
            // { fieldName: 'amountCounterValue', kind: 'number' },
            { fieldName: 'counterValue', kind: 'number' },
            { fieldName: 'profitAndLoss', kind: 'number' },
            { fieldName: 'averagePurchasePrice', kind: 'number' },
            { fieldName: 'variation', kind: 'number' },
            { fieldName: 'counterValueVariation', kind: 'number' },
          ]
        },
        {
          id: 'history',
          fields: [
            { fieldName: 'openingDate', kind: 'date' },
            { fieldName: 'lastPrice', kind: 'amount' },
            { fieldName: 'lastPriceDate', kind: 'date' },
            { fieldName: 'lastMovementDate', kind: 'date' },
            // { fieldName: 'lastPurchaseDate', kind: 'date' },
          ]
        },
        {
          id: 'performance',
          fields: [
            { fieldName: 'perfYearToDatePercent', kind: 'percent' },
            { fieldName: 'portfolioWeight', kind: 'percent' },
            { fieldName: 'assetWeight', kind: 'percent' },
          ]
        }*/
    ]
  },
  BOND: {
    title: 'isin',
    type: 'BOND',
    headerFields: [
      'name', 'currency',
      'locationPlace', // 'instrumentCode',
      { fieldName: 'quantity', kind: 'number' }, // { fieldName: 'purchasePrice', kind: 'amount' },
      'quotationPlaceName', // 'sectorName',
      { fieldName: 'negotiateQuantity', kind: 'number' },
      // { fieldName: 'moodysRating', kind: 'number' }, { fieldName: 'stdAndPoorRating', kind: 'number' },
      // { fieldName: 'rate', kind: 'number' }, // 'bloomBergTicker',
      // 'pledged', 
    ],
    tabs: [
      /*  {
          id: 'amounts',
          fields: [
            { fieldName: 'amount', kind: 'number' },
            // { fieldName: 'amountCounterValue', kind: 'number' },
            { fieldName: 'counterValue', kind: 'number' },
            { fieldName: 'profitAndLoss', kind: 'number' },
            { fieldName: 'currentPrice', kind: 'number' },
            { fieldName: 'variation', kind: 'number' },
            { fieldName: 'counterValueVariation', kind: 'number' },
            { fieldName: 'accruedInterestAmount', kind: 'number' },
            // { fieldName: 'receivedCouponNetAmount', kind: 'number' },
          ]
        },
        {
          id: 'history',
          fields: [
            { fieldName: 'dueDate', kind: 'date' },
            { fieldName: 'lastMovementDate', kind: 'date' },
            // { fieldName: 'lastCouponDate', kind: 'date' },
            // { fieldName: 'callDate', kind: 'date' },
            { fieldName: 'priceDate', kind: 'date' },
          ]
        },
        {
          id: 'performance',
          fields: [
            { fieldName: 'portfolioWeight', kind: 'percent' },
            // { fieldName: 'assetWeight', kind: 'percent' },
            // { fieldName: 'gainMaturityLossVsBuy', kind: 'number' },
          ]
        } */
    ],
  },
  CASH: {
    title: 'isin',
    type: 'CASH',
    headerFields: [],
    tabs: [],
  },
  EQUITY: {
    title: 'isin',
    type: 'EQUITY',
    headerFields: [
      'name', 'currency',
      'locationPlace', // 'instrumentCode',
      { fieldName: 'quantity', kind: 'number' },
      'quotationPlaceName', { fieldName: 'negotiateQuantity', kind: 'number' },
      // 'quotationPlaceMicCode',
      // 'pledged', 'bloomBergTicker',
    ],
    tabs: [
      /*{
        id: 'amounts',
        fields: [
          { fieldName: 'amount', kind: 'number' },
          // { fieldName: 'amountCounterValue', kind: 'number' },
          { fieldName: 'counterValue', kind: 'number' },
          { fieldName: 'profitAndLoss', kind: 'number' },
          { fieldName: 'variation', kind: 'number' },
          // { fieldName: 'counterValueVariation', kind: 'number' },
          { fieldName: 'averagePurchasePrice', kind: 'number' },
          { fieldName: 'unitCostPrice', kind: 'number' },
        ]
      },
      {
        id: 'history',
        fields: [
          { fieldName: 'openingDate', kind: 'date' },
          { fieldName: 'lastMovementDate', kind: 'date' },
          { fieldName: 'lastPurchaseDate', kind: 'date' },
          { fieldName: 'lastPrice', kind: 'number' },
          { fieldName: 'lastPriceDate', kind: 'date' },
        ]
      },
      {
        id: 'performance',
        fields: [
          // { fieldName: 'perfYearToDatePercent', kind: 'percent' },
          { fieldName: 'portfolioWeight', kind: 'percent' },
          { fieldName: 'assetWeight', kind: 'percent' },
        ]
      }*/
    ],
  },
  FORWARD: {
    title: 'backOfficeRef',
    type: 'FORWARD',
    headerFields: [],
    tabs: [],
  },
  MISCELLANEOUS: {
    title: 'isin',
    type: 'MISCELLANEOUS',
    headerFields: [
      'name', 'currency',
      'locationPlace', // 'instrumentCode',
      { fieldName: 'quantity', kind: 'number' },
      // 'underlyingName', 'underlyingIsin',
      'quotationPlaceName', { fieldName: 'negotiateQuantity', kind: 'number' },
      // 'instrument',
      // 'pledged',
    ],
    tabs: [
      /* {
         id: 'amounts',
         fields: [
           { fieldName: 'quantity', kind: 'number' },
           { fieldName: 'amount', kind: 'number' },
           // { fieldName: 'amountCounterValue', kind: 'number' },
           { fieldName: 'counterValue', kind: 'number' },
         ]
       },
       {
         id: 'history',
         fields: [
           { fieldName: 'lastPrice', kind: 'amount' },
           { fieldName: 'lastPriceDate', kind: 'date' },
           { fieldName: 'lastMovementDate', kind: 'date' },
           { fieldName: 'maturityDate', kind: 'date' },
         ]
       },
       {
         id: 'performance',
         fields: [
           { fieldName: 'portfolioWeight', kind: 'percent' },
           { fieldName: 'assetWeight', kind: 'percent' },
         ]
       }*/
    ]
  }
}

export const getTemplate = (positionType: PositionTypeName, isFim: boolean): Template => {
  if (isFim) {
    return templatesForFims[positionType]
  }
  return templates[positionType]
}

export const LinkCellRenderer = ({ value, data }: CustomCellRendererProps) => {
  if (isNotDefined(value) || isNotDefined(data)) {
    return '-';
  }
  return (
    <button
      data-e2e={`instrument-bond-${data.instrumentCode}`}
      type="button"
      className="btn btn-link text-underline p-0"
    >
      {value}
    </button>
  );
}